import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDatatable } from './useDatatable';
import DatatableHeader from './DatatableHeader';
import DatatableTable from './DatatableTable';
import DatatablePagination from './DatatablePagination';
import '../../../css/datatable.css';

const Datatable = React.memo((props) => {
  const dtState = useDatatable(props);
  const {
    titletable = '',
    titleDisabled = false,
    searchEnabled = true,
    searchColumnsEnabled = true,
    nameColumnsUppercase = true,
    adjustColumnSize = false,
    orderColumns = true,
    checkboxColumn = false,
    rowsPerPageOptions = [10, 20, 50, 100],
    manualPageInput = false,
    onRowClick,
    onRowDoubleClick,
    headerActions,
    searchPosition = 'right'
  } = props;

  const handleDragDropReset = () => {
    dtState.setDraggingKey(null);
    dtState.setDragOverKey(null);
    dtState.setDragOverColIdx(null);
  };

  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [dtState.effectivePage]);

  return (
    <div className="d-flex flex-column gap-3 w-100 datatable-container">
      <DatatableHeader 
        titletable={titletable}
        titleDisabled={titleDisabled}
        searchEnabled={searchEnabled}
        globalSearch={dtState.globalSearch}
        handleGlobalSearchChange={dtState.handleGlobalSearchChange}
        handleGlobalSearchKeyDown={dtState.handleGlobalSearchKeyDown}
        handleClearFilters={dtState.handleClearFilters}
        headerActions={headerActions}
        columnFilters={dtState.columnFilters}
        sortBy={dtState.sortBy}
        initialSortBy={props.initialSortBy || props.defaultSortBy}
        cols={dtState.cols}
        toggleColumnVisibility={dtState.toggleColumnVisibility}
        resetLayout={dtState.resetLayout}
        searchPosition={searchPosition}
      />

      <DatatableTable 
        tableContainerRef={tableContainerRef}
        cols={dtState.cols}
        colWidths={dtState.colWidths}
        setColWidths={dtState.setColWidths}
        rows={dtState.rows}
        loadingState={dtState.loadingState}
        effectivePerPage={dtState.effectivePerPage}
        checkboxColumn={checkboxColumn}
        searchColumnsEnabled={searchColumnsEnabled}
        nameColumnsUppercase={nameColumnsUppercase}
        adjustColumnSize={adjustColumnSize}
        rowIdField={props.rowIdField || "id"}
        onRowClick={onRowClick}
        onRowDoubleClick={onRowDoubleClick}
        draggingKey={dtState.draggingKey}
        dragOverKey={dtState.dragOverKey}
        dragOverColIdx={dtState.dragOverColIdx}
        selectedRows={dtState.selectedRows}
        toggleSelectAll={dtState.toggleSelectAll}
        toggleSelectRow={dtState.toggleSelectRow}
        sortBy={dtState.sortBy}
        sortDir={dtState.sortDir}
        handleSortClick={dtState.handleSortClick}
        columnFilters={dtState.columnFilters}
        handleColumnFilterChange={dtState.handleColumnFilterChange}
        inputHoverColIdx={dtState.inputHoverColIdx}
        setInputHoverColIdx={dtState.setInputHoverColIdx}
        orderColumns={orderColumns}
        handleDragStart={dtState.handleDragStart}
        handleDragOver={dtState.handleDragOver}
        handleDragLeave={dtState.handleDragLeave}
        handleDrop={dtState.handleDrop}
        handleDragDropReset={handleDragDropReset}
      />

      <DatatablePagination 
        rows={dtState.rows}
        effectivePage={dtState.effectivePage}
        effectivePerPage={dtState.effectivePerPage}
        effectiveTotal={dtState.effectiveTotal}
        totalPages={dtState.totalPages}
        rowsPerPageOptions={rowsPerPageOptions}
        manualPageInput={manualPageInput}
        manualInputPage={dtState.manualInputPage}
        setManualInputPage={dtState.setManualInputPage}
        handlePageChange={dtState.handlePageChange}
        handlePerPageChange={dtState.handlePerPageChange}
      />
    </div>
  );
});

export default Datatable;

Datatable.propTypes = {
  titletable: PropTypes.string,
  titleDisabled: PropTypes.bool,
  searchEnabled: PropTypes.bool,
  searchColumnsEnabled: PropTypes.bool,
  nameColumnsUppercase: PropTypes.bool,
  adjustColumnSize: PropTypes.bool,
  orderColumns: PropTypes.bool,
  checkboxColumn: PropTypes.bool,
  rowsPerPageOptions: PropTypes.array,
  manualPageInput: PropTypes.bool,
  onRowClick: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  headerActions: PropTypes.node,
  searchPosition: PropTypes.oneOf(['left', 'right', 'center']),
  rowIdField: PropTypes.string,
  initialSortBy: PropTypes.any,
  defaultSortBy: PropTypes.any,
};
